<template>
  <div id="waste-accounting-con">
    <div class="main-content">
      <b-card no-body>
        <b-card class="px-2">
          <div class="waste-accounting-header d-flex justify-content-between">
            <div>
              <h2>Affaldsregnskab</h2>
              <p class="text-muted">Posteringsoversigt</p>
            </div>
            <div class="d-flex justify-content-between">
              <date-range-picker
                ref="picker"
                v-model="dateRange"
                opens="left"
                :locale-data="{ firstDay: 1, format: 'dd/mm/yyyy' }"
                :append-to-body="true"
                @update="handleDateRangeChange"
              />
              <div>
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  class="ml-2"
                  :disabled="isExporting"
                  @click="exportData"
                >
                  <b-spinner v-if="isExporting" small class="mr-1" />
                  <feather-icon
                    v-else
                    icon="DownloadIcon"
                    size="16"
                    class="mr-1"
                  />
                  {{ isExporting ? "Eksporterer..." : "Eksporter" }}
                </b-button>
              </div>
            </div>
          </div>
          <b-row class="mt-4">
            <b-col md="2">
              <b-card class="stat-card">
                <template v-if="isLoadingTotals">
                  <div class="skeleton-value"></div>
                  <div class="skeleton-label"></div>
                </template>
                <template v-else>
                  <div class="stat-value">
                    {{ formatTotalNumber(totals.totalPostings) }}
                  </div>
                  <div class="stat-label">Posteringer</div>
                </template>
              </b-card>
            </b-col>
            <b-col md="2">
              <b-card class="stat-card">
                <template v-if="isLoadingTotals">
                  <div class="skeleton-value"></div>
                  <div class="skeleton-label"></div>
                </template>
                <template v-else>
                  <div class="stat-value">
                    {{ formatTotalTonnage(totals.totalTonnageCollected) }}
                  </div>
                  <div class="stat-label">Tons indsamlet</div>
                </template>
              </b-card>
            </b-col>
            <b-col md="2">
              <b-card class="stat-card">
                <template v-if="isLoadingTotals">
                  <div class="skeleton-value"></div>
                  <div class="skeleton-label"></div>
                </template>
                <template v-else>
                  <div class="stat-value">
                    {{ formatTotalNumber(totals.totalCollections) }}
                  </div>
                  <div class="stat-label">Tømninger</div>
                </template>
              </b-card>
            </b-col>
            <b-col md="2">
              <b-card class="stat-card">
                <template v-if="isLoadingTotals">
                  <div class="skeleton-value"></div>
                  <div class="skeleton-label"></div>
                </template>
                <template v-else>
                  <div class="stat-value">
                    {{ formatTotalCO2(totals.totalAbolitionCO2) }}
                  </div>
                  <div class="stat-label">Tons CO2 udledt</div>
                </template>
              </b-card>
            </b-col>
            <b-col md="2">
              <b-card class="stat-card">
                <template v-if="isLoadingTotals">
                  <div class="skeleton-value"></div>
                  <div class="skeleton-label"></div>
                </template>
                <template v-else>
                  <div class="stat-value text-success">
                    {{ formatTotalAmount(totals.totalDebitAmount) }}
                  </div>
                  <div class="stat-label">Total Debit</div>
                </template>
              </b-card>
            </b-col>
            <b-col md="2">
              <b-card class="stat-card">
                <template v-if="isLoadingTotals">
                  <div class="skeleton-value"></div>
                  <div class="skeleton-label"></div>
                </template>
                <template v-else>
                  <div class="stat-value text-danger">
                    {{ formatTotalAmount(totals.totalCreditAmount) }}
                  </div>
                  <div class="stat-label">Total Kredit</div>
                </template>
              </b-card>
            </b-col>
          </b-row>
        </b-card>

        <b-row class="">
          <b-col md="12">
            <div class="px-2">
              <!-- Filter Toggle Button -->
              <div
                class="mb-3 d-flex justify-content-between align-items-center"
              >
                <div>
                  <b-button
                    v-b-toggle.filter-collapse
                    variant="flat-secondary"
                    size="sm"
                  >
                    <feather-icon icon="FilterIcon" size="14" class="mr-1" />
                    {{ filtersVisible ? "Skjul filtre" : "Vis filtre" }}
                    <b-badge
                      v-if="activeFilterCount > 0"
                      variant="primary"
                      class="ml-2"
                    >
                      {{ activeFilterCount }}
                    </b-badge>
                  </b-button>
                </div>
                <div class="d-flex align-items-center">
                  <label class="mb-0 mr-2" style="font-size: 0.875rem"
                    >Vis billagstekst</label
                  >
                  <b-form-checkbox v-model="showVoucherLineText" switch />
                </div>
              </div>

              <!-- Collapsible Filter Controls -->
              <b-collapse id="filter-collapse" v-model="filtersVisible">
                <b-card
                  class="mb-3"
                  style="background: rgba(130, 134, 139, 0.12)"
                >
                  <b-row>
                    <b-col md="1">
                      <b-form-group label="Kategorier" label-size="sm">
                        <b-dropdown
                          size="sm"
                          variant="outline-secondary"
                          class="w-100 category-dropdown"
                          menu-class="category-dropdown-menu"
                          toggle-class="form-control-sm"
                          no-caret
                        >
                          <template #button-content>
                            <div
                              class="d-flex justify-content-between align-items-center w-100"
                            >
                              <span
                                v-if="
                                  tempFilters.postingCategories.length === 0
                                "
                                class="text-muted"
                              >
                                Alle
                              </span>
                              <span v-else>
                                {{ tempFilters.postingCategories.length }} valgt
                              </span>
                              <feather-icon icon="ChevronDownIcon" size="14" />
                            </div>
                          </template>
                          <b-dropdown-form>
                            <b-form-checkbox-group
                              v-model="tempFilters.postingCategories"
                              :options="postingTypeOptions"
                              stacked
                            />
                          </b-dropdown-form>
                          <b-dropdown-divider />
                          <b-dropdown-item-button
                            size="sm"
                            @click="selectAllCategories"
                          >
                            Vælg alle
                          </b-dropdown-item-button>
                          <b-dropdown-item-button
                            size="sm"
                            @click="clearCategories"
                          >
                            Ryd valg
                          </b-dropdown-item-button>
                        </b-dropdown>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Lokationer" label-size="sm">
                        <b-dropdown
                          size="sm"
                          variant="outline-secondary"
                          class="w-100 location-dropdown"
                          menu-class="location-dropdown-menu"
                          toggle-class="form-control-sm"
                          no-caret
                        >
                          <template #button-content>
                            <div
                              class="d-flex justify-content-between align-items-center w-100"
                            >
                              <span
                                v-if="tempFilters.locationIds.length === 0"
                                class="text-muted"
                              >
                                Alle
                              </span>
                              <span v-else>
                                {{ tempFilters.locationIds.length }} valgt
                              </span>
                              <feather-icon icon="ChevronDownIcon" size="14" />
                            </div>
                          </template>
                          <b-dropdown-form>
                            <b-form-checkbox-group
                              v-model="tempFilters.locationIds"
                              :options="locationOptions"
                              stacked
                            />
                          </b-dropdown-form>
                          <b-dropdown-divider />
                          <b-dropdown-item-button
                            size="sm"
                            @click="selectAllLocations"
                          >
                            Vælg alle
                          </b-dropdown-item-button>
                          <b-dropdown-item-button
                            size="sm"
                            @click="clearLocations"
                          >
                            Ryd valg
                          </b-dropdown-item-button>
                        </b-dropdown>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Infokort" label-size="sm">
                        <b-dropdown
                          size="sm"
                          variant="outline-secondary"
                          class="w-100 infocard-dropdown"
                          menu-class="infocard-dropdown-menu"
                          toggle-class="form-control-sm"
                          no-caret
                        >
                          <template #button-content>
                            <div
                              class="d-flex justify-content-between align-items-center w-100"
                            >
                              <span
                                v-if="tempFilters.infocardIds.length === 0"
                                class="text-muted"
                              >
                                Vælg infokort
                              </span>
                              <span v-else>
                                {{ tempFilters.infocardIds.length }} valgt
                              </span>
                              <feather-icon icon="ChevronDownIcon" size="14" />
                            </div>
                          </template>
                          <b-dropdown-form>
                            <div
                              v-for="(group, locationName) in groupedInfocards"
                              :key="locationName"
                              class="infocard-group"
                            >
                              <div class="group-header">
                                {{ locationName || "Uden lokation" }}
                              </div>
                              <b-form-checkbox
                                v-for="infocard in group"
                                :key="infocard.id"
                                v-model="tempFilters.infocardIds"
                                :value="infocard.id"
                                class="ml-3"
                              >
                                {{ infocard.name }} ({{ infocard.refId }})
                              </b-form-checkbox>
                            </div>
                          </b-dropdown-form>
                          <b-dropdown-divider />
                          <b-dropdown-item-button
                            size="sm"
                            @click="selectAllInfocards"
                          >
                            Vælg alle
                          </b-dropdown-item-button>
                          <b-dropdown-item-button
                            size="sm"
                            @click="clearInfocards"
                          >
                            Ryd valg
                          </b-dropdown-item-button>
                        </b-dropdown>
                      </b-form-group>
                    </b-col>
                    <b-col md="3">
                      <b-form-group label="Fraktioner" label-size="sm">
                        <b-dropdown
                          size="sm"
                          variant="outline-secondary"
                          class="w-100 fraction-dropdown"
                          menu-class="fraction-dropdown-menu"
                          toggle-class="form-control-sm"
                          no-caret
                        >
                          <template #button-content>
                            <div
                              class="d-flex justify-content-between align-items-center w-100"
                            >
                              <span
                                v-if="tempFilters.fractionIds.length === 0"
                                class="text-muted"
                              >
                                Vælg fraktioner
                              </span>
                              <span v-else>
                                {{ tempFilters.fractionIds.length }} valgt
                              </span>
                              <feather-icon icon="ChevronDownIcon" size="14" />
                            </div>
                          </template>
                          <b-dropdown-form>
                            <b-form-checkbox-group
                              v-model="tempFilters.fractionIds"
                              :options="fractionOptions"
                              stacked
                            />
                          </b-dropdown-form>
                          <b-dropdown-divider />
                          <b-dropdown-item-button
                            size="sm"
                            @click="selectAllFractions"
                          >
                            Vælg alle
                          </b-dropdown-item-button>
                          <b-dropdown-item-button
                            size="sm"
                            @click="clearFractions"
                          >
                            Ryd valg
                          </b-dropdown-item-button>
                        </b-dropdown>
                      </b-form-group>
                    </b-col>
                    <b-col md="2">
                      <b-form-group label="Bilagsnummer" label-size="sm">
                        <b-form-input
                          v-model="tempFilters.voucherNumber"
                          size="sm"
                          placeholder="Søg bilagsnummer"
                          @keyup.enter="applyFilters"
                        />
                      </b-form-group>
                    </b-col>

                    <b-col md="12" class="d-flex align-items-end">
                      <b-button
                        variant="primary"
                        size="sm"
                        class="mr-2"
                        @click="applyFilters"
                      >
                        <feather-icon icon="CheckIcon" size="14" class="mr-1" />
                        Anvend
                      </b-button>
                      <b-button
                        variant="outline-secondary"
                        size="sm"
                        @click="clearAllFilters"
                      >
                        <feather-icon icon="XIcon" size="14" class="mr-1" />
                        Ryd
                      </b-button>
                    </b-col>
                  </b-row>
                </b-card>
              </b-collapse>
            </div>

            <b-table
              :items="ledgerEntries"
              :fields="tableFields"
              :busy="isLoading"
              show-empty
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :per-page="perPage"
              :empty-text="T('Web.Generic.TableEmpty', 'No items to show')"
              class="general-ledger-table"
              :tbody-tr-class="getRowClass"
            >
              <template #table-busy>
                <div class="text-center text-primary my-5">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Indlæser data...</strong>
                </div>
              </template>
              <template #cell(entryDate)="data">
                {{ formatDate(data.value) }}
              </template>
              <template #cell(entryCategory)="data">
                <b-badge :variant="getCategoryVariant(data.value)">{{
                  getCategoryText(data.value)
                }}</b-badge>
              </template>
              <template #cell(infocardName)="data">
                {{ data.value }}
                <small class="text-muted">{{ data.item.infocardRefId }}</small>
              </template>
              <template #cell(locationName)="data">
                <span class="font-weight-bold">{{ data.value }}</span>
              </template>
              <template #cell(voucherNumber)="data">
                <a
                  href="#"
                  class="text-primary font-weight-bold"
                  @click.prevent="openReconciliationModal(data.item)"
                >
                  {{ data.value || "-" }}
                </a>
              </template>
              <template #cell(collections)="data">
                <span
                  class="text-right"
                  :class="
                    isNullOrEmpty(data.value) ? 'text-dark' : 'font-weight-bold'
                  "
                >
                  {{ formatCollections(data.value) }}
                </span>
              </template>
              <template #cell(tonnageCollected)="data">
                <span
                  class="text-right"
                  :class="
                    isNullOrEmpty(data.value) ? 'text-dark' : 'font-weight-bold'
                  "
                >
                  {{ formatTonnage(data.value) }}
                </span>
              </template>
              <template #cell(abolitionCO2)="data">
                <span
                  class="text-right"
                  :class="
                    isNullOrEmpty(data.value) ? 'text-dark' : 'font-weight-bold'
                  "
                >
                  {{ formatCO2(data.value) }}
                </span>
              </template>
              <template #cell(unitPrice)="data">
                {{ formatAmount(data.value) }}
              </template>

              <template #cell(debitAmount)="data">
                <span
                  class="text-right"
                  :class="
                    isNullOrEmpty(data.value)
                      ? 'text-dark'
                      : 'text-success font-weight-bold'
                  "
                >
                  {{ formatAmount(data.value) }}
                </span>
              </template>
              <template #cell(creditAmount)="data">
                <span
                  class="text-right"
                  :class="
                    isNullOrEmpty(data.value)
                      ? 'text-dark'
                      : 'text-danger font-weight-bold'
                  "
                >
                  {{ formatAmount(data.value) }}
                </span>
              </template>
              <template #cell(voucherLineText)="data">
                <small>{{ data.value }}</small>
              </template>
            </b-table>

            <b-pagination
              v-model="currentPage"
              :total-rows="totalEntries"
              :per-page="perPage"
              align="center"
              class="mt-3"
              @change="handlePageChange"
            />
          </b-col>
        </b-row>
      </b-card>
    </div>

    <!-- Reconciliation Modal -->
    <b-modal
      :id="`reconciliation-modal-${selectedVoucherId}`"
      size="xl"
      centered
      scrollable
      :title="`#${selectedVoucherNumber}`"
      header-text-variant="light"
      hide-footer
    >
      <simple-reconciliation-view
        v-if="selectedVoucherId"
        :reconciliation-id="selectedVoucherId"
        :highlight-line-id="selectedVoucherLineId"
        @soft-save-clicked="closeReconciliationModal"
        @saved-and-closed="closeReconciliationModal"
        @go-back="closeReconciliationModal"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BButton,
  BBadge,
  BProgress,
  BRow,
  BCol,
  BOverlay,
  BPagination,
  BModal,
  BSpinner,
  BFormGroup,
  BFormSelect,
  BFormSelectOption,
  BFormInput,
  BCollapse,
  BDropdown,
  BDropdownForm,
  BDropdownItemButton,
  BDropdownDivider,
  BFormCheckboxGroup,
  BFormCheckbox,
  VBToggle,
} from "bootstrap-vue";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import ToastificationContent from "@/@core/components/toastification/ToastificationContent.vue";
import ResourceService from "@/services/base/resource.service";
import SimpleReconciliationView from "@/views/economics/reconciliation/SimpleReconciliationView.vue";
import * as XLSX from "xlsx";

export default {
  directives: {
    "b-toggle": VBToggle,
  },
  components: {
    BCard,
    BTable,
    BButton,
    BBadge,
    BProgress,
    BRow,
    BCol,
    BOverlay,
    BPagination,
    BModal,
    BSpinner,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BFormInput,
    BCollapse,
    BDropdown,
    BDropdownForm,
    BDropdownItemButton,
    BDropdownDivider,
    BFormCheckboxGroup,
    BFormCheckbox,
    DateRangePicker,
    ToastificationContent,
    SimpleReconciliationView,
  },
  data() {
    return {
      isLoading: false,
      isLoadingTotals: false,
      dateRange: {
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: new Date(),
      },
      ledgerEntries: [],
      totalEntries: 0,
      totals: {
        totalPostings: 0,
        totalTonnageCollected: 0,
        totalCollections: 0,
        totalAbolitionCO2: 0,
        totalCreditAmount: 0,
        totalDebitAmount: 0,
      },
      currentPage: 1,
      perPage: 30,
      sortBy: "entryDate",
      sortDesc: true,
      selectedVoucherId: null,
      selectedVoucherNumber: null,
      selectedVoucherLineId: null,
      ledgerService: new ResourceService("concern/accounting/ledger-entries"),
      filtersVisible: false,
      filters: {
        postingCategories: [],
        voucherNumber: "",
        infocardIds: [],
        fractionIds: [],
        locationIds: [],
      },
      tempFilters: {
        postingCategories: [],
        voucherNumber: "",
        infocardIds: [],
        fractionIds: [],
        locationIds: [],
      },
      filterOptions: {
        infocards: [],
        fractions: [],
        locations: [],
      },
      showVoucherLineText: false,
      isExporting: false,
    };
  },
  computed: {
    tableFields() {
      const fields = [
        {
          key: "entryDate",
          label: "Dato",
          sortable: false,
        },
        {
          key: "entryCategory",
          label: "Kategori",
          sortable: false,
        },
        {
          key: "voucherNumber",
          label: "Bilagsnummer",
          sortable: false,
        },
      ];

      // Conditionally add voucherLineText field
      if (this.showVoucherLineText) {
        fields.push({
          key: "voucherLineText",
          label: "Billagstekst",
        });
      }

      // Add remaining fields
      fields.push(
        {
          key: "infocardName",
          label: "Infokort",
          sortable: false,
        },
        {
          key: "fractionName",
          label: "Fraktion",
          sortable: false,
        },
        {
          key: "locationName",
          label: "Lokation",
          sortable: false,
        },
        {
          key: "collections",
          label: "Tømninger",
          sortable: false,
          class: "text-right",
        },
        {
          key: "tonnageCollected",
          label: "Tons indsamlet",
          sortable: false,
          class: "text-right",
        },
        {
          key: "abolitionCO2",
          label: "CO2 besparelse",
          sortable: false,
          class: "text-right",
        },
        {
          key: "unitPrice",
          label: "Enhedspris",
          sortable: false,
          class: "text-right",
        },
        {
          key: "debitAmount",
          label: "Debit",
          sortable: false,
          class: "text-right",
        },
        {
          key: "creditAmount",
          label: "Kredit",
          sortable: false,
          class: "text-right",
        }
      );

      return fields;
    },
    postingTypeOptions() {
      // Convert postingTypes object to array of options for checkbox group
      return Object.entries(this.postingTypes || {}).map(([value, text]) => ({
        value,
        text,
      }));
    },
    locationOptions() {
      // Convert locations array to options for checkbox group
      return this.filterOptions.locations.map((location) => ({
        value: location.id,
        text: location.name,
      }));
    },
    fractionOptions() {
      // Convert fractions array to options for checkbox group
      return this.filterOptions.fractions.map((fraction) => ({
        value: fraction.id,
        text: fraction.name,
      }));
    },
    filteredInfocards() {
      // If no locations are selected in temp filters, show all infocards
      if (
        !this.tempFilters.locationIds ||
        this.tempFilters.locationIds.length === 0
      ) {
        return this.filterOptions.infocards;
      }

      // Filter infocards by selected location IDs
      return this.filterOptions.infocards.filter(
        (infocard) =>
          infocard.locationId &&
          this.tempFilters.locationIds.includes(infocard.locationId)
      );
    },
    groupedInfocards() {
      // Group filtered infocards by locationName
      const grouped = {};
      this.filteredInfocards.forEach((infocard) => {
        const location = infocard.locationName || "Uden lokation";
        if (!grouped[location]) {
          grouped[location] = [];
        }
        grouped[location].push(infocard);
      });
      // Sort locations alphabetically
      const sortedGrouped = {};
      Object.keys(grouped)
        .sort()
        .forEach((key) => {
          sortedGrouped[key] = grouped[key];
        });
      return sortedGrouped;
    },
    activeFilterCount() {
      let count = 0;
      if (
        this.filters.postingCategories &&
        this.filters.postingCategories.length > 0
      )
        count++;
      if (this.filters.voucherNumber) count++;
      if (this.filters.infocardIds && this.filters.infocardIds.length > 0)
        count++;
      if (this.filters.fractionIds && this.filters.fractionIds.length > 0)
        count++;
      if (this.filters.locationIds && this.filters.locationIds.length > 0)
        count++;
      return count;
    },
  },
  watch: {
    "tempFilters.locationIds": function (newLocationIds) {
      // When location selection changes, remove any selected infocards
      // that don't belong to the selected locations
      if (newLocationIds && newLocationIds.length > 0) {
        const visibleInfocardIds = this.filteredInfocards.map((i) => i.id);
        this.tempFilters.infocardIds = this.tempFilters.infocardIds.filter(
          (id) => visibleInfocardIds.includes(id)
        );
      }
    },
  },
  mounted() {
    this.loadFilterOptions();
    this.loadEntries();
    this.loadTotals();
  },
  methods: {
    getFilterParams() {
      // Helper method to build filter parameters consistently
      const params = {
        fromDate: this.dateRange.startDate.toISOString(),
        toDate: this.dateRange.endDate.toISOString(),
      };

      // Add filters to params if they have values
      if (
        this.filters.postingCategories &&
        this.filters.postingCategories.length > 0
      ) {
        params.postingCategories = this.filters.postingCategories;
      }
      if (this.filters.voucherNumber) {
        params.voucherNumber = this.filters.voucherNumber;
      }
      if (this.filters.infocardIds && this.filters.infocardIds.length > 0) {
        params.infocardIds = this.filters.infocardIds;
      }
      if (this.filters.fractionIds && this.filters.fractionIds.length > 0) {
        params.fractionIds = this.filters.fractionIds;
      }
      if (this.filters.locationIds && this.filters.locationIds.length > 0) {
        params.locationIds = this.filters.locationIds;
      }

      return params;
    },
    async loadEntries() {
      this.isLoading = true;
      try {
        // Get base filter params and add pagination
        const params = {
          ...this.getFilterParams(),
          page: this.currentPage,
          perPage: this.perPage,
        };

        const { data } = await this.ledgerService.get("", params);

        if (data && data.data) {
          this.ledgerEntries = data.data.items || [];

          const paging = data.data.paging || {};
          this.totalEntries = paging.totalItems || 0;
        }
      } catch (error) {
        console.error("Error loading ledger entries:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.T("Web.WasteAccounting.Error", "Fejl"),
            text: this.T(
              "Web.WasteAccounting.LoadError",
              "Kunne ikke hente posteringer"
            ),
            variant: "danger",
            icon: "AlertTriangleIcon",
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadTotals() {
      this.isLoadingTotals = true;
      try {
        // Use the same filter params as loadEntries (without pagination)
        const params = this.getFilterParams();

        const { data } = await this.ledgerService.get("totals", params);

        if (data && data.data) {
          this.totals = {
            totalPostings: data.data.totalPostings ?? 0,
            totalTonnageCollected: data.data.totalTonnageCollected ?? 0,
            totalCollections: data.data.totalCollections ?? 0,
            totalAbolitionCO2: data.data.totalAbolitionCO2 ?? 0,
            totalCreditAmount: data.data.totalCreditAmount ?? 0,
            totalDebitAmount: data.data.totalDebitAmount ?? 0,
          };
        }
      } catch (error) {
        console.error("Error loading totals:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.T("Web.WasteAccounting.Error", "Fejl"),
            text: this.T(
              "Web.WasteAccounting.LoadError",
              "Kunne ikke hente totaler"
            ),
            variant: "danger",
            icon: "AlertTriangleIcon",
          },
        });
      } finally {
        this.isLoadingTotals = false;
      }
    },
    async loadFilterOptions() {
      try {
        const { data } = await this.ledgerService.get("filter-options");
        if (data && data.data) {
          this.filterOptions = {
            infocards: data.data.infocards || [],
            fractions: data.data.fractions || [],
            locations: data.data.locations || [],
          };
        }
      } catch (error) {
        console.error("Error loading filter options:", error);
      }
    },
    handleDateRangeChange() {
      this.currentPage = 1; // Reset to first page when date changes
      this.loadEntries();
      this.loadTotals();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.loadEntries();
    },
    applyFilters() {
      // Copy temp filters to active filters
      this.filters = { ...this.tempFilters };
      this.currentPage = 1; // Reset to first page when filters change
      this.loadEntries();
      this.loadTotals();
      // Collapse the filter section after applying
      this.filtersVisible = false;
    },
    clearAllFilters() {
      // Clear both temp and active filters
      this.tempFilters = {
        postingCategories: [],
        voucherNumber: "",
        infocardIds: [],
        fractionIds: [],
        locationIds: [],
      };
      this.filters = {
        postingCategories: [],
        voucherNumber: "",
        infocardIds: [],
        fractionIds: [],
        locationIds: [],
      };
      this.currentPage = 1;
      this.loadEntries();
      this.loadTotals();
    },
    selectAllCategories() {
      // Select all posting types
      this.tempFilters.postingCategories = Object.keys(this.postingTypes || {});
    },
    clearCategories() {
      // Clear all selected categories
      this.tempFilters.postingCategories = [];
    },
    selectAllLocations() {
      // Select all locations
      this.tempFilters.locationIds = this.filterOptions.locations.map(
        (l) => l.id
      );
    },
    clearLocations() {
      // Clear all selected locations
      this.tempFilters.locationIds = [];
    },
    selectAllInfocards() {
      // Select all currently visible (filtered) infocards
      this.tempFilters.infocardIds = this.filteredInfocards.map((i) => i.id);
    },
    clearInfocards() {
      // Clear all selected infocards
      this.tempFilters.infocardIds = [];
    },
    selectAllFractions() {
      // Select all fractions
      this.tempFilters.fractionIds = this.filterOptions.fractions.map(
        (f) => f.id
      );
    },
    clearFractions() {
      // Clear all selected fractions
      this.tempFilters.fractionIds = [];
    },
    getCategoryVariant(category) {
      const variants = {
        Rent: "primary",
        Charge: "danger",
        WasteCollection: "warning",
        Income: "success",
        WrongSorting: "danger",
        FutileDriving: "secondary",
        GoodsPurchase: "info",
        RepairPurchase: "secondary",
        SuppliesPurchase: "info",
        DeliveryOrRepatriation: "primary",
        Exchange: "warning",
        RegistrationFee: "dark",
        OilSurchargeFee: "secondary",
        OtherFees: "dark",
        ContainerWash: "info",
        Reprocessing: "info",
        RoadToll: "secondary",
      };
      return variants[category] || "dark";
    },
    getRowClass(item, type) {
      if (!item || type !== "row") return "";

      const categoryClasses = {
        Rent: "row-rent",
        Charge: "row-charge",
        WasteCollection: "row-waste-collection",
        Income: "row-income",
        WrongSorting: "row-wrong-sorting",
        FutileDriving: "row-futile-driving",
        GoodsPurchase: "row-goods-purchase",
        RepairPurchase: "row-repair-purchase",
        SuppliesPurchase: "row-supplies-purchase",
        DeliveryOrRepatriation: "row-delivery",
        Exchange: "row-exchange",
        RegistrationFee: "row-registration-fee",
        OilSurchargeFee: "row-oil-surcharge",
        OtherFees: "row-other-fees",
        ContainerWash: "row-container-wash",
        Reprocessing: "row-reprocessing",
        RoadToll: "row-road-toll",
      };

      return categoryClasses[item.entryCategory] || "";
    },
    getCategoryText(category) {
      // Use the global postingTypes from main.js mixin
      if (this.postingTypes && this.postingTypes[category]) {
        return this.postingTypes[category];
      }

      // Handle RoadToll which is not in postingTypes
      if (category === "RoadToll") {
        return this.T("Web.Generic.PostingCategories.RoadToll", "Vejafgift");
      }

      // Fallback to the raw category name
      return category;
    },
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return new Intl.DateTimeFormat("da-DK", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(d);
    },
    formatNumber(value) {
      // If null, undefined, or 0, return "0"
      if (value == null || value === 0) return "0";
      // Format as integer with thousands separator using Danish locale
      return new Intl.NumberFormat("da-DK", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
    formatCollections(value) {
      console.log(value);
      // If null, undefined, empty, or 0, return "--"
      if (
        value == null ||
        value == "null" ||
        value == "" ||
        value === 0 ||
        value === "0"
      )
        return "";
      // Return integer with no decimals or thousands separator
      return Math.floor(value).toString();
    },
    formatTonnage(value) {
      // If null, undefined, empty, or 0, return "--"
      if (
        value == null ||
        value == "null" ||
        value == "" ||
        value === 0 ||
        value === "0"
      )
        return "";
      // Format with 3 decimal places using Danish locale
      return new Intl.NumberFormat("da-DK", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(value);
    },
    formatCO2(value) {
      // If null, undefined, empty string, or 0, return "--"
      if (
        value == null ||
        value === "" ||
        value === "null" ||
        value === 0 ||
        value === "0"
      )
        return "";
      // Format with 3 decimal places using Danish locale
      return new Intl.NumberFormat("da-DK", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(value);
    },
    formatAmount(value) {
      // If null, undefined, empty, or 0, return "--"
      if (
        value == null ||
        value === "" ||
        value === "null" ||
        value === 0 ||
        value === "0"
      )
        return "";
      // Format with 2 decimal places using Danish locale
      return new Intl.NumberFormat("da-DK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    isNullOrEmpty(value) {
      return (
        value == null ||
        value === "" ||
        value === "null" ||
        value === 0 ||
        value === "0"
      );
    },
    // Formatting methods for totals (show 0 instead of empty string)
    formatTotalNumber(value) {
      // If null or undefined, return "0"
      if (value == null) return "0";
      // Format as integer with thousands separator using Danish locale
      return new Intl.NumberFormat("da-DK", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
    formatTotalTonnage(value) {
      // If null or undefined, return "0,000"
      if (value == null) return "0,000";
      // Format with 3 decimal places using Danish locale
      return new Intl.NumberFormat("da-DK", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(value);
    },
    formatTotalCO2(value) {
      // If null or undefined, return "0,000"
      if (value == null) return "0,000";
      // Format with 3 decimal places using Danish locale
      return new Intl.NumberFormat("da-DK", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(value);
    },
    formatTotalAmount(value) {
      // If null or undefined, return "0,00"
      if (value == null) return "0,00";
      // Format with 2 decimal places using Danish locale
      return new Intl.NumberFormat("da-DK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    async exportData() {
      this.isExporting = true;
      try {
        // Get base filter params and set perPage to totalEntries to fetch all records
        const params = {
          ...this.getFilterParams(),
          page: 1,
          perPage: this.totalEntries || 10000, // Use totalEntries or a large number as fallback
        };

        const { data } = await this.ledgerService.get("", params);

        if (data && data.data && data.data.items) {
          const entries = data.data.items;

          // Prepare data for Excel
          const excelData = entries.map((entry) => ({
            Dato: this.formatDate(entry.entryDate),
            Kategori: this.getCategoryText(entry.entryCategory),
            Bilagsnummer: entry.voucherNumber || "",
            Billagstekst: entry.voucherLineText || "",
            Infokort: entry.infocardName || "",
            "Infokort RefID": entry.infocardRefId || "",
            Fraktion: entry.fractionName || "",
            Lokation: entry.locationName || "",
            Tømninger: entry.collections || 0,
            "Tons indsamlet": entry.tonnageCollected || 0,
            "CO2 besparelse": entry.abolitionCO2 || 0,
            Enhedspris: entry.unitPrice || 0,
            Debit: entry.debitAmount || 0,
            Kredit: entry.creditAmount || 0,
          }));

          // Create workbook and worksheet
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(excelData);

          // Auto-size columns
          const colWidths = [
            { wch: 12 }, // Dato
            { wch: 20 }, // Kategori
            { wch: 15 }, // Bilagsnummer
            { wch: 30 }, // Billagstekst
            { wch: 25 }, // Infokort
            { wch: 15 }, // Infokort RefID
            { wch: 20 }, // Fraktion
            { wch: 25 }, // Lokation
            { wch: 10 }, // Tømninger
            { wch: 15 }, // Tons indsamlet
            { wch: 15 }, // CO2 besparelse
            { wch: 12 }, // Enhedspris
            { wch: 12 }, // Debit
            { wch: 12 }, // Kredit
          ];
          ws["!cols"] = colWidths;

          // Add worksheet to workbook
          XLSX.utils.book_append_sheet(wb, ws, "Affaldsregnskab");

          // Generate filename with current date
          const today = new Date();
          const dateStr = today.toISOString().split("T")[0];
          const filename = `affaldsregnskab_${dateStr}.xlsx`;

          // Write and download the file
          XLSX.writeFile(wb, filename);

          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Eksport gennemført",
              text: `${entries.length} posteringer eksporteret til ${filename}`,
              variant: "success",
              icon: "CheckIcon",
            },
          });
        }
      } catch (error) {
        console.error("Error exporting data:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Eksport fejlede",
            text: "Kunne ikke eksportere data. Prøv igen senere.",
            variant: "danger",
            icon: "AlertTriangleIcon",
          },
        });
      } finally {
        this.isExporting = false;
      }
    },
    openReconciliationModal(item) {
      // Check if item has voucherId, if not use voucherNumber or another identifier
      const voucherId = item.voucherId || item.invoiceId || item.id;
      const voucherLineId = item.voucherLineId || null;

      if (!voucherId) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.T("Web.WasteAccounting.Error", "Fejl"),
            text: this.T(
              "Web.WasteAccounting.NoVoucherFound",
              "Ingen bilag fundet for denne postering"
            ),
            variant: "danger",
            icon: "AlertTriangleIcon",
          },
        });
        return;
      }

      this.selectedVoucherId = voucherId;
      this.selectedVoucherNumber = item.voucherNumber;
      this.selectedVoucherLineId = voucherLineId;
      this.$nextTick(() => {
        this.$bvModal.show(`reconciliation-modal-${voucherId}`);
      });
    },
    closeReconciliationModal() {
      if (this.selectedVoucherId) {
        this.$bvModal.hide(`reconciliation-modal-${this.selectedVoucherId}`);
        this.selectedVoucherId = null;
        this.selectedVoucherLineId = null;
        // Reload entries to reflect any changes
        this.loadEntries();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#waste-accounting-con {
  .waste-accounting-header {
    padding: 1rem 0;
    border-bottom: 1px solid #ebe9f1;
    margin-bottom: 1.5rem;

    h2 {
      margin-bottom: 0.5rem;
    }
  }

  .stat-card {
    text-align: center;
    padding: 1rem;
    min-height: 90px;

    .stat-value {
      font-size: 1.5rem;
      font-weight: 600;
      color: #7367f0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.text-danger {
        color: #ea5455 !important;
      }

      &.text-success {
        color: #28c76f !important;
      }

      &.text-primary {
        color: #7367f0 !important;
      }
    }

    .stat-label {
      margin-top: 0.5rem;
      font-size: 0.75rem;
      color: #6e6b7b;
      white-space: nowrap;
    }

    .skeleton-value {
      height: 24px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
      border-radius: 4px;
      margin: 0 auto;
      width: 80%;
    }

    .skeleton-label {
      height: 14px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
      border-radius: 4px;
      margin: 0.5rem auto 0;
      width: 60%;
    }
  }

  @keyframes skeleton-loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  // All dropdown filters styles to match b-form-select
  ::v-deep .category-dropdown,
  ::v-deep .location-dropdown,
  ::v-deep .infocard-dropdown,
  ::v-deep .fraction-dropdown {
    .dropdown-toggle {
      background-color: #fff;
      border: 1px solid #d8d6de;
      color: #6e6b7b;
      font-size: 0.857rem;
      padding: 0.438rem 0.7rem;
      height: calc(1.25em + 0.876rem + 2px);
      text-align: left;
      font-weight: 400;

      &:hover {
        background-color: #fff;
        border-color: #d8d6de;
        color: #6e6b7b;
      }

      &:focus {
        background-color: #fff;
        border-color: #7367f0;
        box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
        color: #6e6b7b;
      }

      &.show {
        background-color: #fff;
        border-color: #7367f0;
        box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
        color: #6e6b7b;
      }

      &::after {
        display: none;
      }
    }
  }

  ::v-deep .category-dropdown-menu,
  ::v-deep .location-dropdown-menu,
  ::v-deep .infocard-dropdown-menu,
  ::v-deep .fraction-dropdown-menu {
    max-height: 300px;
    overflow-y: auto;
    min-width: 250px;
    border: 1px solid #d8d6de;
    box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);

    .b-dropdown-form {
      padding: 0.5rem;

      .custom-control {
        padding-left: 1.5rem;
        margin-bottom: 0.25rem;
        font-size: 0.857rem;

        &:hover {
          background-color: #f8f9fa;
        }
      }

      .custom-control-label {
        width: 100%;
        cursor: pointer;
        padding: 0.25rem;
        color: #6e6b7b;
      }
    }

    .dropdown-item-button {
      font-size: 0.857rem;
      padding: 0.5rem 1rem;
      color: #6e6b7b;

      &:hover {
        background-color: #f8f9fa;
        color: #7367f0;
      }
    }
  }

  // Infocard dropdown specific styles for grouping
  ::v-deep .infocard-dropdown-menu {
    .infocard-group {
      margin-bottom: 1rem;

      &:last-child {
        margin-bottom: 0;
      }

      .group-header {
        font-weight: 600;
        font-size: 0.75rem;
        color: #4e5154;
        text-transform: uppercase;
        padding: 0.25rem 0.5rem;
        background-color: #f3f2f7;
        margin-bottom: 0.25rem;
        border-radius: 0.25rem;
      }

      .custom-control {
        margin-left: 1rem;
      }
    }
  }

  .general-ledger-table {
    ::v-deep {
      .text-right {
        text-align: right !important;
      }

      th {
        background-color: #f8f8f8;
        font-weight: 600;
      }

      tbody tr:hover {
        filter: brightness(0.95);
      }

      // Very light background colors for each category
      .row-rent {
        background-color: rgba(
          115,
          103,
          240,
          0.05
        ); // Primary - very light purple
      }

      .row-charge {
        background-color: rgba(234, 84, 85, 0.05); // Danger - very light red
      }

      .row-waste-collection {
        background-color: rgba(
          255,
          159,
          67,
          0.05
        ); // Warning - very light orange
      }

      .row-income {
        background-color: rgba(
          40,
          199,
          111,
          0.05
        ); // Success - very light green
      }

      .row-wrong-sorting {
        background-color: rgba(
          234,
          84,
          85,
          0.07
        ); // Danger - slightly darker light red
      }

      .row-futile-driving {
        background-color: rgba(
          130,
          134,
          139,
          0.05
        ); // Secondary - very light gray
      }

      .row-goods-purchase {
        background-color: rgba(0, 207, 232, 0.05); // Info - very light cyan
      }

      .row-repair-purchase {
        background-color: rgba(130, 134, 139, 0.07); // Secondary - light gray
      }

      .row-supplies-purchase {
        background-color: rgba(0, 207, 232, 0.07); // Info - light cyan
      }

      .row-delivery {
        background-color: rgba(115, 103, 240, 0.07); // Primary - light purple
      }

      .row-exchange {
        background-color: rgba(255, 159, 67, 0.07); // Warning - light orange
      }

      .row-registration-fee {
        background-color: rgba(70, 70, 70, 0.05); // Dark - very light dark gray
      }

      .row-oil-surcharge {
        background-color: rgba(130, 134, 139, 0.06); // Secondary - light gray
      }

      .row-other-fees {
        background-color: rgba(70, 70, 70, 0.06); // Dark - light dark gray
      }

      .row-container-wash {
        background-color: rgba(0, 207, 232, 0.06); // Info - light cyan
      }

      .row-reprocessing {
        background-color: rgba(
          0,
          207,
          232,
          0.08
        ); // Info - slightly darker light cyan
      }

      .row-road-toll {
        background-color: rgba(
          130,
          134,
          139,
          0.08
        ); // Secondary - slightly darker light gray
      }
    }
  }
}
</style>